import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Redirect } from 'react-router-dom';
import Switch from 'react-switch';
import Loading from '../ui/Loading.js';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Utils from '../../commons/utils/Utils';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

const BASE_URL = '/inventario/items';

/**
 * VEC-3312 — ABM del catálogo de ítems de tipo Producto (repuestos).
 *
 * `tipo` es fijo "Producto" y de solo lectura: este catálogo es el de repuestos, y el de
 * Mano de Obra es la story hermana VEC-3432. Dejarlo editable permitiría crear ítems de MO
 * desde acá y pisaría ese alcance.
 */
const TIPO_PRODUCTO = 'Producto';

const FORM_INIT = {
  id: 0,
  numeroDeParte: '',
  externalCode: '',
  descripcion: '',
  categoria: '',
  subcategoria: '',
  unidad: '',
  marca: '',
  modelo: '',
  referencia: '',
  costo: '',
  valorMaximo: '',
  costoFijo: false,
  stockMinimo: '',
  activo: true,
};

const InventarioItemsAbm = (props) => {
  const intl = useIntl();
  const [redirectTo, setRedirectTo] = useState(null);
  const [formData, setFormData] = useState({ ...FORM_INIT });
  const [loading, setLoading] = useState(false);

  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [marcas, setMarcas] = useState([]);

  const isView = props.action === 'VIEW';

  useEffect(() => {
    if (
      ((Security.hasPermission('INVENTARIO_ITEMS_CREAR') && props.action === 'ADD') ||
        (Security.hasPermission('INVENTARIO_ITEMS_MODIFICAR') && props.action === 'EDIT') ||
        (Security.hasPermission('INVENTARIO_ITEMS_VISUALIZAR') && props.action === 'VIEW')) &&
      ConfigBusiness.get('inventarioItems.habilitado') === 'true'
    ) {
      initSelects();
      initForm();
    } else {
      setRedirectTo('/error');
    }
  }, [props.action, props.match && props.match.params && props.match.params.id]);

  const initSelects = () => {
    Utils.getData('/panol2/select/categorias')
      .then((data) => setCategorias(data || []))
      .catch(() => setCategorias([]));
    Utils.getData('/panol2/select/unidadMedida')
      .then((data) => setUnidades(data || []))
      .catch(() => setUnidades([]));
    Utils.getData('/panol2/select/marcas')
      .then((data) => setMarcas(data || []))
      .catch(() => setMarcas([]));
  };

  const fetchSubcategorias = (categoriaId) => {
    if (!categoriaId) {
      setSubcategorias([]);
      return;
    }
    Utils.getData('/panol2/select/subcategorias?categoria=' + categoriaId)
      .then((data) => setSubcategorias(data || []))
      .catch(() => setSubcategorias([]));
  };

  const initForm = () => {
    if (props.action === 'ADD') return;
    setLoading(true);
    Utils.getData(`${BASE_URL}/${props.match.params.id}`)
      .then((data) => {
        if (data) {
          setFormData({
            id: data.id,
            numeroDeParte: data.numeroDeParte !== null && data.numeroDeParte !== undefined ? data.numeroDeParte : '',
            externalCode: data.externalCode !== null && data.externalCode !== undefined ? data.externalCode : '',
            descripcion: data.descripcion !== null && data.descripcion !== undefined ? data.descripcion : '',
            categoria: data.categoriaId !== null && data.categoriaId !== undefined ? data.categoriaId : '',
            subcategoria: data.subcategoriaId !== null && data.subcategoriaId !== undefined ? data.subcategoriaId : '',
            unidad: data.unidadId !== null && data.unidadId !== undefined ? data.unidadId : '',
            marca: data.marcaId !== null && data.marcaId !== undefined ? data.marcaId : '',
            modelo: data.modelo !== null && data.modelo !== undefined ? data.modelo : '',
            referencia: data.referencia !== null && data.referencia !== undefined ? data.referencia : '',
            costo: data.costo !== null && data.costo !== undefined ? data.costo : '',
            valorMaximo: data.valorMaximo !== null && data.valorMaximo !== undefined ? data.valorMaximo : '',
            costoFijo: data.costoFijo ? true : false,
            stockMinimo: data.stockMinimo !== null && data.stockMinimo !== undefined ? data.stockMinimo : '',
            activo: data.activo ? true : false,
          });
          fetchSubcategorias(data.categoriaId);
        } else {
          exit();
        }
      })
      .catch(() => exit())
      .finally(() => setLoading(false));
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoriaChange = (value) => {
    setFormData((prev) => ({ ...prev, categoria: value, subcategoria: '' }));
    fetchSubcategorias(value);
  };

  // Los mensajes se resuelven con `id` literal para que `formatjs extract` los pueda tomar:
  // pasarle el id como variable los dejaría fuera del catálogo y sin poder traducirse.
  const warn = (message) => {
    Swal.fire({ icon: 'warning', title: message, confirmButtonText: 'OK' });
  };

  const validate = () => {
    if (!formData.numeroDeParte || !String(formData.numeroDeParte).trim()) {
      warn(intl.formatMessage({ id: 'inventarioItems.error.numero_de_parte_required', defaultMessage: 'El número de parte es requerido' }));
      return false;
    }
    if (!formData.descripcion || !String(formData.descripcion).trim()) {
      warn(intl.formatMessage({ id: 'inventarioItems.error.descripcion_required', defaultMessage: 'La descripción es requerida' }));
      return false;
    }
    if (!formData.categoria) {
      warn(intl.formatMessage({ id: 'inventarioItems.error.categoria_required', defaultMessage: 'La categoría es requerida' }));
      return false;
    }
    if (!formData.subcategoria) {
      warn(intl.formatMessage({ id: 'inventarioItems.error.subcategoria_required', defaultMessage: 'La subcategoría es requerida' }));
      return false;
    }
    if (!formData.unidad) {
      warn(intl.formatMessage({ id: 'inventarioItems.error.unidad_required', defaultMessage: 'La unidad de medida es requerida' }));
      return false;
    }
    if (formData.stockMinimo === '' || formData.stockMinimo === null || formData.stockMinimo === undefined) {
      warn(intl.formatMessage({ id: 'inventarioItems.error.stock_minimo_required', defaultMessage: 'El stock mínimo es requerido' }));
      return false;
    }
    if (isNaN(Number(formData.stockMinimo)) || Number(formData.stockMinimo) < 0) {
      warn(intl.formatMessage({ id: 'inventarioItems.error.stock_minimo_invalido', defaultMessage: 'El stock mínimo debe ser un número mayor o igual a cero' }));
      return false;
    }
    if (formData.costo !== '' && (isNaN(Number(formData.costo)) || Number(formData.costo) < 0)) {
      warn(intl.formatMessage({ id: 'inventarioItems.error.costo_invalido', defaultMessage: 'El costo debe ser un número mayor o igual a cero' }));
      return false;
    }
    if (formData.valorMaximo !== '' && isNaN(Number(formData.valorMaximo))) {
      warn(intl.formatMessage({ id: 'inventarioItems.error.valor_maximo_invalido', defaultMessage: 'El valor máximo debe ser un número' }));
      return false;
    }
    // VEC-3498: se avisa acá para no hacer ir y volver al servidor, pero la autoridad es el
    // backend (ItemService::validarCostoNoSuperaValorMaximo), que valida igual.
    if (
      formData.valorMaximo !== '' &&
      Number(formData.valorMaximo) > 0 &&
      formData.costo !== '' &&
      Number(formData.costo) > Number(formData.valorMaximo)
    ) {
      warn(
        intl.formatMessage(
          {
            id: 'inventarioItems.error.costo_supera_valor_maximo',
            defaultMessage: 'El costo supera el valor máximo permitido ({max}).',
          },
          { max: formData.valorMaximo }
        )
      );
      return false;
    }
    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading(true);

    const dataToSend = {
      numeroDeParte: String(formData.numeroDeParte).trim(),
      externalCode: formData.externalCode !== '' ? String(formData.externalCode).trim() : null,
      descripcion: String(formData.descripcion).trim(),
      categoria: formData.categoria,
      subcategoria: formData.subcategoria,
      unidad: formData.unidad,
      tipo: TIPO_PRODUCTO,
      marca: formData.marca !== '' ? formData.marca : null,
      modelo: formData.modelo !== '' ? String(formData.modelo).trim() : null,
      referencia: formData.referencia !== '' ? String(formData.referencia).trim() : null,
      costo: formData.costo !== '' ? Number(formData.costo) : 0,
      // VEC-3498: vacío = sin tope. El backend colapsa vacío/0/negativo a null.
      valorMaximo: formData.valorMaximo !== '' ? Number(formData.valorMaximo) : null,
      costoFijo: formData.costoFijo,
      stockMinimo: Number(formData.stockMinimo),
      activo: formData.activo,
    };

    // Sin permiso de desactivación el estado no se manda: lo conserva el backend.
    if (props.action === 'EDIT' && !Security.hasPermission('INVENTARIO_ITEMS_DESACTIVAR')) {
      delete dataToSend.activo;
    }

    Utils.sendData(
      `${BASE_URL}${props.action !== 'ADD' ? '/' + formData.id : ''}`,
      props.action === 'ADD' ? 'POST' : 'PUT',
      dataToSend
    )
      .then((response) => {
        if (response) exit();
      })
      // Utils.sendData ya muestra el error por Swal; acá solo se corta la propagación.
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const exit = () => {
    setRedirectTo(BASE_URL);
  };

  const labelOf = (options, value) => {
    for (let i = 0; i < options.length; i++) {
      if (String(options[i].value) === String(value)) return options[i].label;
    }
    return '-';
  };

  const renderText = (name, label, required) => (
    <div className="col-md-6">
      <div className="form-group row">
        <label className="col-md-4 label-control col-form-label" htmlFor={name}>
          {label}
          {required ? '*' : ''}:
        </label>
        <div className="col-md-8">
          {isView ? (
            <div className="form-control-static col-form-label form-value">
              {formData[name] !== '' && formData[name] !== null ? formData[name] : '-'}
            </div>
          ) : (
            <input
              type="text"
              className="form-control"
              id={name}
              name={name}
              value={formData[name]}
              onChange={(e) => handleInputChange(name, e.target.value)}
            />
          )}
        </div>
      </div>
    </div>
  );

  const renderNumber = (name, label, required) => (
    <div className="col-md-6">
      <div className="form-group row">
        <label className="col-md-4 label-control col-form-label" htmlFor={name}>
          {label}
          {required ? '*' : ''}:
        </label>
        <div className="col-md-8">
          {isView ? (
            <div className="form-control-static col-form-label form-value">
              {formData[name] !== '' && formData[name] !== null ? formData[name] : '-'}
            </div>
          ) : (
            <input
              type="number"
              min="0"
              step="0.01"
              className="form-control"
              id={name}
              name={name}
              value={formData[name]}
              onChange={(e) => handleInputChange(name, e.target.value)}
            />
          )}
        </div>
      </div>
    </div>
  );

  const renderSelect = (name, label, options, required, onChange, disabled) => (
    <div className="col-md-6">
      <div className="form-group row">
        <label className="col-md-4 label-control col-form-label" htmlFor={name}>
          {label}
          {required ? '*' : ''}:
        </label>
        <div className="col-md-8">
          {isView ? (
            <div className="form-control-static col-form-label form-value">{labelOf(options, formData[name])}</div>
          ) : (
            <select
              className="form-control"
              id={name}
              name={name}
              value={formData[name]}
              disabled={disabled}
              onChange={(e) => (onChange ? onChange(e.target.value) : handleInputChange(name, e.target.value))}
            >
              <option value="">{intl.formatMessage({ id: 'Seleccione', defaultMessage: 'Seleccione...' })}</option>
              {options.map((option) => (
                <option key={name + '_' + option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      {redirectTo && <Redirect push to={redirectTo} />}
      {loading && <Loading />}
      <div className="row">
        <div className="col-md-12">
          <form className="form form-horizontal">
            <div className="form-body">
              <div className="card pull-up">
                <div className="card-content">
                  <div className="card-body">
                    <h4 className="form-section">
                      <i className="icon-home"></i>
                      {intl.formatMessage({
                        id: 'inventarioItems.abm.header_datos_generales',
                        defaultMessage: 'Datos Generales',
                      })}
                      <div className="float-right" style={{ fontSize: '14px' }}>
                        *{' '}
                        {intl.formatMessage({
                          id: 'inventarioItems.abm.campos_requeridos',
                          defaultMessage: 'campos requeridos',
                        })}
                      </div>
                    </h4>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label">
                            {intl.formatMessage({
                              id: 'inventarioItems.abm.tipo_de_item',
                              defaultMessage: 'Tipo de ítem',
                            })}
                            :
                          </label>
                          <div className="col-md-8">
                            <div className="form-control-static col-form-label form-value">
                              {intl.formatMessage({
                                id: 'inventarioItems.abm.tipo_producto',
                                defaultMessage: 'Producto',
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                      {renderText(
                        'numeroDeParte',
                        intl.formatMessage({ id: 'inventarioItems.abm.numero_de_parte', defaultMessage: 'Nº de parte' }),
                        true
                      )}
                    </div>

                    <div className="row">
                      {renderText(
                        'descripcion',
                        intl.formatMessage({ id: 'inventarioItems.abm.descripcion', defaultMessage: 'Descripción' }),
                        true
                      )}
                      {renderText(
                        'externalCode',
                        intl.formatMessage({ id: 'inventarioItems.abm.gtin', defaultMessage: 'GTIN' }),
                        false
                      )}
                    </div>

                    <div className="row">
                      {renderSelect(
                        'categoria',
                        intl.formatMessage({ id: 'inventarioItems.abm.categoria', defaultMessage: 'Categoría' }),
                        categorias,
                        true,
                        handleCategoriaChange,
                        false
                      )}
                      {renderSelect(
                        'subcategoria',
                        intl.formatMessage({ id: 'inventarioItems.abm.subcategoria', defaultMessage: 'Subcategoría' }),
                        subcategorias,
                        true,
                        null,
                        !formData.categoria
                      )}
                    </div>

                    <div className="row">
                      {renderSelect(
                        'unidad',
                        intl.formatMessage({ id: 'inventarioItems.abm.unidad_de_medida', defaultMessage: 'Unidad de medida' }),
                        unidades,
                        true,
                        null,
                        false
                      )}
                      {renderNumber(
                        'stockMinimo',
                        intl.formatMessage({ id: 'inventarioItems.abm.stock_minimo', defaultMessage: 'Stock mínimo' }),
                        true
                      )}
                    </div>

                    <div className="row">
                      {renderSelect(
                        'marca',
                        intl.formatMessage({ id: 'inventarioItems.abm.marca', defaultMessage: 'Marca' }),
                        marcas,
                        false,
                        null,
                        false
                      )}
                      {renderText(
                        'modelo',
                        intl.formatMessage({ id: 'inventarioItems.abm.modelo', defaultMessage: 'Modelo' }),
                        false
                      )}
                    </div>

                    <div className="row">
                      {renderText(
                        'referencia',
                        intl.formatMessage({ id: 'inventarioItems.abm.referencia', defaultMessage: 'Referencia' }),
                        false
                      )}
                      {renderNumber(
                        'costo',
                        intl.formatMessage({ id: 'inventarioItems.abm.costo', defaultMessage: 'Costo' }),
                        false
                      )}
                    </div>

                    <div className="row">
                      {renderNumber(
                        'valorMaximo',
                        intl.formatMessage({ id: 'inventarioItems.abm.valor_maximo', defaultMessage: 'Valor máximo' }),
                        false
                      )}
                      <div className="col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label" htmlFor="costoFijo">
                            {intl.formatMessage({ id: 'inventarioItems.abm.costo_fijo', defaultMessage: 'Costo fijo' })}
                          </label>
                          <div className="col-md-8">
                            <Switch
                              onChange={(estado) => handleInputChange('costoFijo', estado)}
                              checked={formData.costoFijo ? true : false}
                              id="costoFijo"
                              name="costoFijo"
                              disabled={isView}
                              offColor="#FF4961"
                              onColor="#28D094"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-12">
                        <small className="text-muted">
                          <FormattedMessage
                            id="inventarioItems.abm.ayuda_valor_maximo"
                            defaultMessage="Valor máximo: tope del costo de este ítem. Dejarlo vacío significa sin tope."
                          />
                        </small>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label" htmlFor="activo">
                            {intl.formatMessage({ id: 'inventarioItems.abm.activo', defaultMessage: 'Activo' })}
                          </label>
                          <div className="col-md-8">
                            <Switch
                              onChange={(estado) => handleInputChange('activo', estado)}
                              checked={formData.activo ? true : false}
                              id="activo"
                              name="activo"
                              disabled={
                                isView ||
                                (props.action === 'EDIT' && !Security.hasPermission('INVENTARIO_ITEMS_DESACTIVAR'))
                              }
                              offColor="#FF4961"
                              onColor="#28D094"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card pull-up">
                <div className="card-content">
                  <div className="card-body">
                    <div className="text-cd text-right">
                      {!isView && (
                        <button onClick={handleSubmit} className="btn btn-primary mr-1">
                          <i className="fa fa-check-circle"></i>{' '}
                          {intl.formatMessage({ id: 'inventarioItems.abm.guardar', defaultMessage: 'Guardar' })}
                        </button>
                      )}
                      <button type="button" className="btn btn-danger" onClick={() => exit()}>
                        <i className="fa fa-times-circle"></i>{' '}
                        {isView
                          ? intl.formatMessage({ id: 'inventarioItems.abm.volver', defaultMessage: 'Volver' })
                          : intl.formatMessage({ id: 'inventarioItems.abm.cancelar', defaultMessage: 'Cancelar' })}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};

export default InventarioItemsAbm;
